import React, { FC, useCallback } from "react";

import { defineMessages, useIntl } from "react-intl";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import { ShapeItemType } from "@mapmycustomers/shared/types/map/PinLegend";
import { ButtonLink, Popconfirm } from "@mapmycustomers/ui";

import { DEFAULT_ITEMS_SHAPE } from "@app/scene/map/components/pinLegends/utils/consts";

import ShapeOptionItem from "../ShapeOptionItem";

import styles from "./CustomShapes.module.scss";

const messages = defineMessages({
  header: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.theme.custom.header",
    defaultMessage: "Custom Buckets",
    description: "Header of the custom shapes section",
  },
  label: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.custom.item.label",
    defaultMessage: "Level {index}{type, select, lowest { (Lowest)} highest { (Highest)} other {}}",
  },
  labelUnknown: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.custom.item.label.unknown",
    defaultMessage: "Unknown (no value)",
    description: "Label of an item used to set a default color",
  },
  resetAll: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.theme.custom.header.resetAll",
    defaultMessage: "Reset All",
    description: "Reset All button title in the Header of the custom shapes section",
  },
  resetCancel: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.theme.custom.header.resetAll.confirmation.cancel",
    defaultMessage: "Cancel",
    description: "Reset All confirmation Cancel option in the Header of the custom shapes section",
  },
  resetMessage: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.theme.custom.header.resetAll.confirmation",
    defaultMessage: "Are you sure you want to reset all shapes to be “Square”?",
    description: "Reset All confirmation message option in the Header of the custom shapes section",
  },
  resetOk: {
    id: "map.colorization.createEditSidebar.styleEditor.numeric.shapes.theme.custom.header.resetAll.confirmation.ok",
    defaultMessage: "Reset",
    description: "Reset All confirmation Ok option in the Header of the custom shapes section",
  },
  shapesDescription: {
    id: "map.colorization.createEditSidebar.styleEditor.shapes.theme.description",
    defaultMessage:
      "Each unique value has been assigned a shape automatically. Click the indicator to select another shape.",
    description: "Description of the Shapes step in the style editor of a pin legend",
  },
});

export interface CustomShapesProps {
  defaultShape: MarkerShape;
  onChange?: (colors: ShapeItemType[]) => void;
  onDefaultShapeChange?: (color: MarkerShape) => void;
  shapes: ShapeItemType[];
}

const CustomShapes: FC<CustomShapesProps> = ({
  defaultShape,
  onChange,
  onDefaultShapeChange,
  shapes,
}) => {
  const intl = useIntl();

  const handleShapeChange = useCallback(
    (color: ShapeItemType, index: number) => {
      onChange?.(shapes.map((item, itemIndex) => (itemIndex === index ? color : item)));
    },
    [shapes, onChange]
  );

  const handleResetShapes = useCallback(() => {
    onChange?.(shapes.map((item) => ({ ...item, shape: DEFAULT_ITEMS_SHAPE })));
  }, [onChange, shapes]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>{intl.formatMessage(messages.header)}</div>
        <Popconfirm
          cancelText={intl.formatMessage(messages.resetCancel)}
          description={intl.formatMessage(messages.resetMessage)}
          okText={intl.formatMessage(messages.resetOk)}
          onConfirm={handleResetShapes}
          placement="topLeft"
        >
          <ButtonLink bold>{intl.formatMessage(messages.resetAll)}</ButtonLink>
        </Popconfirm>
      </div>
      <div className={styles.description}>{intl.formatMessage(messages.shapesDescription)}</div>
      <div className={styles.list}>
        <ShapeOptionItem<ShapeItemType>
          key="unknown"
          label={intl.formatMessage(messages.labelUnknown)}
          onChange={(value: ShapeItemType) => onDefaultShapeChange?.(value.shape)}
          value={{ shape: defaultShape }}
        />
        {shapes.map((item, index) => (
          <ShapeOptionItem<ShapeItemType>
            key={index}
            label={intl.formatMessage(messages.label, {
              index: index + 1,
              type: index === 0 ? "lowest" : index === shapes.length - 1 ? "highest" : "",
            })}
            onChange={(value: ShapeItemType) => handleShapeChange(value, index)}
            // This should NOT be necessary, but there are still some pin legends with null shape
            value={item.shape ? item : { shape: DEFAULT_ITEMS_SHAPE }}
          />
        ))}
      </div>
    </div>
  );
};

export default CustomShapes;
